import { useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LOGO_URL } from "../lib/brand";
import { LogOut, BarChart3, Activity, Filter } from "lucide-react";
import TrackRecordEditor from "../components/admin/TrackRecordEditor";
import AnalyticsView from "../components/admin/AnalyticsView";
import FunnelView from "../components/admin/FunnelView";
import AdminTopNav from "../components/AdminTopNav";

const TABS = [
  { id: "trackrecord", label: "Track Record", icon: BarChart3 },
  { id: "analytics", label: "Analytics", icon: Activity },
  { id: "funnel", label: "Funnel", icon: Filter },
];export default function AdminTrackRecord() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("alphalane_admin_token") : null;

  const activeTab = useMemo(() => {
    const t = searchParams.get("tab");
    return TABS.find((x) => x.id === t)?.id || "trackrecord";
  }, [searchParams]);

  useEffect(() => {
    if (!token) navigate("/admin/login");
  }, [token, navigate]);

  const setTab = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === "trackrecord") next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  const logout = () => {
    localStorage.removeItem("alphalane_admin_token");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)]">
      <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Alphalane" className="h-9 w-9 object-contain" />
            <div className="font-display font-semibold text-[15px]">
              <span className="al-grad-text">ALPHALANE</span>
              <span className="text-slate-500 text-xs ml-2 font-medium uppercase tracking-widest">Admin</span>
            </div>
          </Link>
          <button onClick={logout} data-testid="admin-logout" className="al-cta-ghost !py-2 !px-3 text-sm">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      {/* Top-level navigation */}
      <AdminTopNav />

      {/* Sub-tabs inside the Performance dashboard */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-5">
        <div className="inline-flex p-1 rounded-full bg-white border border-[var(--al-line)] shadow-sm" data-testid="admin-subtabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-subtab-${t.id}`}
                className={`px-4 py-1.5 rounded-full text-sm font-display font-semibold flex items-center gap-2 transition-all ${
                  active
                    ? "al-grad-bg text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        {activeTab === "trackrecord" && <TrackRecordEditor />}
        {activeTab === "analytics" && <AnalyticsView />}
        {activeTab === "funnel" && <FunnelView />}
      </main>

      <style>{`
        .al-input-sm { width: 100%; padding: 8px 12px; border-radius: 10px; border: 1px solid var(--al-line); background: white; font-family: 'Manrope', sans-serif; font-size: 14px; color: var(--al-ink); transition: border-color 200ms, box-shadow 200ms; }
        .al-input-sm:focus { outline: none; border-color: var(--al-purple); box-shadow: 0 0 0 3px rgba(139,92,246,0.10); }
        .al-input-sm:disabled { background: #FBFAFF; color: #94A3B8; }
        .al-input-sm[readonly] { background: #FBFAFF; color: #475569; cursor: not-allowed; }
      `}</style>
    </div>
  );
}
